import { Component, Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';

/** Path of the hidden detour route. Never shown in the address bar. */
export const FORM_RELOAD_PATH = '_reload';

/**
 * Empty routed component the detour lands on for a single tick. It renders
 * nothing and has no guards, so the user only ever sees the create form
 * disappear and come back blank.
 */
@Component({ selector: 'app-form-reload-blank', standalone: true, template: '' })
export class FormReloadBlankComponent {}

/**
 * Re-opens a create form as a brand-new component instance.
 *
 * Operators raise several documents in a row (bulk breakups, POs, GRNs,
 * transfer outs), so after a save the form should come back blank instead
 * of bouncing to the list page. The create components are large and keep
 * state in dozens of fields, subjects and cached lookups; resetting them by
 * hand is fragile. Angular reuses the routed component when the URL doesn't
 * change, so a plain re-navigate would keep all of it. Detouring through a
 * blank route (with `skipLocationChange`, so the address bar never moves)
 * makes the router destroy and rebuild the component, which resets
 * everything - including the next-number preview - exactly as if the user
 * had come from the list page.
 *
 * Callers must have already marked their form as saved so the
 * PendingChangesGuard lets the detour through without a discard prompt.
 */
@Injectable({ providedIn: 'root' })
export class FormReloadService {
  private router = inject(Router);

  /** @param createUrl absolute path of the create form, e.g. `/po/create-po`. */
  restart(createUrl: string): Promise<boolean> {
    return this.router
      .navigateByUrl('/' + FORM_RELOAD_PATH, { skipLocationChange: true })
      .then(() => this.router.navigateByUrl(createUrl));
  }
}
